import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'react-hot-toast';
import { 
  Truck, ArrowLeft, Play, CheckCircle2, Clock, 
  MapPin, Package, Calendar, User, Phone, Hash,
  Loader2, ExternalLink, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShipmentView = () => {
  const navigate = useNavigate();
  const { shipmentNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);


  const loadShipment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/warehouse/shipments/${shipmentNumber}`);
      setShipment(response.data);
    } catch (error) {
      console.error('Error loading shipment:', error);
      toast.error('Failed to load shipment');
      navigate('/warehouse/shipments');
    } finally {
      setLoading(false);
    }
  }, [shipmentNumber, navigate]);

  useEffect(() => {
    loadShipment();
  }, [loadShipment]);

  const handleStartShipment = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/warehouse/shipments/${shipmentNumber}/start?started_by=ADMIN`);
      toast.success('Shipment started - In Transit');
      loadShipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start shipment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteShipment = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/warehouse/shipments/${shipmentNumber}/complete?completed_by=ADMIN`);
      toast.success('Shipment completed - Delivered');
      loadShipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete shipment');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'PLANNED': 'bg-orange-100 text-orange-800 border-orange-200',
      'IN_TRANSIT': 'bg-orange-100 text-orange-800 border-orange-200',
      'DELIVERED': 'bg-green-100 text-green-800 border-green-200',
      'CANCELLED': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PLANNED': return <Clock className="w-5 h-5" />;
      case 'IN_TRANSIT': return <Truck className="w-5 h-5" />;
      case 'DELIVERED': return <CheckCircle2 className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center h-[70vh]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Layout>
    );
  }

  if (!shipment) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">Shipment not found</p>
          <Button onClick={() => navigate('/warehouse/shipments')} className="mt-4">
            Back to Shipments
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="shipment-view-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/warehouse/shipments')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Truck className="w-7 h-7 text-orange-600" />
                Shipment: {shipment.shipment_number}
              </h1>
              <p className="text-sm text-gray-500">
                Created: {shipment.created_at ? new Date(shipment.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${getStatusColor(shipment.status)}`}>
              {getStatusIcon(shipment.status)}
              <span className="font-medium">{shipment.status?.replace(/_/g, ' ')}</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-1 space-y-6">
            {/* Shipment Info */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Shipment Details</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-gray-500">Shipping Point</p>
                    <p className="font-medium">{shipment.shipping_point || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Truck className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-gray-500">Carrier</p>
                    <p className="font-medium">{shipment.carrier_name || shipment.carrier_id || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-gray-500">Planned Departure</p>
                    <p className="font-medium">{shipment.planned_departure || shipment.planned_ship_date || 'N/A'}</p>
                  </div>
                </div>
                {shipment.planned_arrival && (
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Expected Arrival</p>
                      <p className="font-medium">{shipment.planned_arrival}</p>
                    </div>
                  </div>
                )}
                {shipment.tracking_number && (
                  <div className="flex items-center gap-3 text-sm">
                    <Hash className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Tracking Number</p>
                      <p className="font-medium">{shipment.tracking_number}</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Vehicle & Driver */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Vehicle & Driver</h3>
              <div className="space-y-3">
                {shipment.vehicle_number && (
                  <div className="flex items-center gap-3 text-sm">
                    <Truck className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Vehicle Number</p>
                      <p className="font-medium">{shipment.vehicle_number}</p>
                    </div>
                  </div>
                )}
                {shipment.driver_name && (
                  <div className="flex items-center gap-3 text-sm">
                    <User className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Driver Name</p>
                      <p className="font-medium">{shipment.driver_name}</p>
                    </div>
                  </div>
                )}
                {(shipment.driver_contact || shipment.driver_phone) && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Driver Contact</p>
                      <p className="font-medium">{shipment.driver_contact || shipment.driver_phone}</p>
                    </div>
                  </div>
                )}
                {!shipment.vehicle_number && !shipment.driver_name && (
                  <p className="text-sm text-gray-400">No vehicle/driver assigned</p>
                )}
              </div>
            </Card>

            {/* Actions */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Actions</h3>
              <div className="space-y-3">
                {shipment.status === 'PLANNED' && (
                  <Button 
                    onClick={handleStartShipment} 
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Start Shipment
                  </Button>
                )}
                {shipment.status === 'IN_TRANSIT' && (
                  <Button 
                    onClick={handleCompleteShipment} 
                    className="w-full bg-green-600 hover:bg-green-700"
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Complete Delivery
                  </Button>
                )}
                {shipment.status === 'DELIVERED' && (
                  <div className="text-center py-4 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    <p className="text-sm font-medium text-green-800">Shipment Delivered</p>
                    {shipment.actual_arrival && (
                      <p className="text-xs text-green-600 mt-1">
                        Delivered on: {shipment.actual_arrival}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Summary */}
            <Card className="p-6 bg-orange-50 border-orange-200">
              <h3 className="text-lg font-semibold mb-4 text-orange-800">Summary</h3>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-orange-800">{shipment.total_deliveries || shipment.deliveries?.length || 0}</p>
                  <p className="text-xs text-gray-600">Deliveries</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-800">{shipment.total_handling_units || 0}</p>
                  <p className="text-xs text-gray-600">Handling Units</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Deliveries */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Deliveries in Shipment</h3>
              
              {(!shipment.deliveries || shipment.deliveries.length === 0) ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No deliveries in this shipment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {shipment.deliveries.map((delivery, index) => (
                    <div
                      key={delivery.delivery_number || index}
                      className="p-4 border rounded-lg hover:border-orange-200 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-gray-900">
                              {delivery.delivery_number}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              delivery.delivery_status === 'ASSIGNED' 
                                ? 'bg-orange-100 text-orange-800' 
                                : delivery.delivery_status === 'DELIVERED'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {delivery.delivery_status || 'ASSIGNED'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3 h-3" />
                              <span>{delivery.ship_to_name || delivery.ship_to_party || 'N/A'}</span>
                            </div>
                            {delivery.ship_to_address && (
                              <div className="flex items-center gap-2">
                                <MapPin className="w-3 h-3" />
                                <span className="truncate">{delivery.ship_to_address}</span>
                              </div>
                            )}
                          </div>
                          {delivery.handling_units && delivery.handling_units.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500">
                                Handling Units: {delivery.handling_units.length}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                            Stop #{delivery.stop_sequence || index + 1}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/otc/deliveries/${delivery.delivery_number}`)}
                            className="text-orange-600"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Route/Legs if available */}
            {shipment.legs && shipment.legs.length > 0 && (
              <Card className="p-6 mt-6">
                <h3 className="text-lg font-semibold mb-4">Route Legs</h3>
                <div className="relative">
                  {shipment.legs.map((leg, index) => (
                    <div key={`leg-${leg.leg_number || leg.id || index}`} className="flex items-start gap-4 pb-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full ${
                          leg.status === 'COMPLETED' ? 'bg-green-500' : 'bg-gray-300'
                        }`} />
                        {index < shipment.legs.length - 1 && (
                          <div className="w-0.5 h-full bg-gray-200 my-1" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{leg.location || `Leg ${index + 1}`}</p>
                        <p className="text-xs text-gray-500">{leg.arrival_time || 'N/A'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShipmentView;
